// ProfileRewardsPanel — U4 (2026-07-09). Rewards & referrals, folded into
// Profile from the retired top-level /rewards tab.
//
// What survived and what died (owner directive + audit §3.3):
//   * Offers browse + opt-in  — KEPT (reuses BrowseOffersPanel verbatim;
//     the machinery is live and platform offers still need a home).
//   * Exposure page           — KILLED. The formula chip, "risk meter" and
//     modelling jargon are gone; replaced by ONE plain-words card:
//     "Offers have cost you ₹X so far. The most they can ever cost: ₹Y."
//   * Referrals               — KEPT (reuses ReferralsPanel), lives at the
//     bottom as the least-actionable section.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { T } from '@baaligames/shared-ui/admin/lib/textStyles';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import BrowseOffersPanel from '../rewards/BrowseOffersPanel';
import ReferralsPanel from '../rewards/ReferralsPanel';

const inr = (n: number) => `₹${Math.abs(n).toLocaleString('en-IN')}`;

function SectionLabel({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div className={`flex items-center gap-1.5 px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
      <Icon name={icon} size={14} className="text-amber-600 dark:text-yellow-300" />
      {children}
    </div>
  );
}

export default function ProfileRewardsPanel() {
  const t = useT();

  // The two numbers the old exposure page buried under a formula.
  const [cost, setCost] = useState<{ actual: number; max: number } | null>(null);
  useEffect(() => {
    let alive = true;
    adminApi.get<{ summary: { actual_spend_total: number; max_projected_total: number } }>(
      '/admin/me-agent/offers/exposure'
    )
      .then((r) => {
        if (!alive) return;
        setCost({
          actual: Number(r.summary?.actual_spend_total || 0),
          max: Number(r.summary?.max_projected_total || 0),
        });
      })
      .catch(() => { /* the cost card simply doesn't render */ });
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-5">
      {/* ── Offers (browse + opt-in) ─────────────────────────────── */}
      <div>
        <SectionLabel icon="redeem">{t('agent.profile.rewards.offers.section')}</SectionLabel>
        <BrowseOffersPanel />
      </div>

      {/* ── What offers cost me — ONE plain sentence, no formulas. ── */}
      {cost && (cost.actual > 0 || cost.max > 0) && (
        <div>
          <SectionLabel icon="payments">{t('agent.profile.rewards.cost.title')}</SectionLabel>
          <div className="rounded-2xl p-4 bg-white dark:bg-arena-700/40
                          border border-arena-200/60 dark:border-arena-600/40
                          shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
            <p className={`text-[13.5px] leading-relaxed ${T.secondary}`}>
              {t('agent.profile.rewards.cost.line', { x: inr(cost.actual), y: inr(cost.max) })}
            </p>
          </div>
        </div>
      )}

      {/* ── Referrals (read-only network growth) ─────────────────── */}
      <div>
        <SectionLabel icon="hub">{t('agent.profile.rewards.referrals.section')}</SectionLabel>
        <ReferralsPanel />
      </div>
    </div>
  );
}
